<template>
    <div v-click-outside="hidePopup">
        <button ref="menuButtonContainer"
                @click="openPopup()"
                :style="'min-width:' + minWidth + 'px'"
                :class="[buttonClass]">

            <!--If any selections have been made we display the first 2 +x more items as the text-->
            <span v-if="selectedOptions.length">{{ selectedOptionsText }}</span>

            <!--If no selections have been made yet we'll display whatever the was specified as the button text-->
            <span v-else class="text-gray-700">{{ buttonText }}</span>

            <icon-chevron-down
                class="ml-1 h-4 w-4 transform transition duration-300"
                :class="show ? 'rotate-90' : ''">
            </icon-chevron-down>

            <!--This label will hover at the top when there is a selection made so that the user can still see what this dropdown relates to-->
            <transition name="fade">
                <label v-if="selectedOptions.length > 0" for="menuButtonContainer" :class="hoverLabelClass">
                    {{ hoverLabelValue }}
                </label>
            </transition>
        </button>

        <!-- Dropdown Options -->
        <transition name="fade">
            <div v-show="show"
                 class="z-50 items-left bg-white mt-2 p-2 rounded-md shadow-xl ring-1 ring-black ring-opacity-5"
                 ref="menuPopup"
                 style="border-top: 3px solid rgb(226, 27, 34); max-height: 60vh">
                <div class="flex flex-col gap-2">
                    <div>
                        <!--Search box to filter the results if user has not set the search box to none-->
                        <input type="text" v-if="searchType !== 'none'"
                               ref="search_input"
                               v-model="filterText"
                               placeholder="Search..."
                               class="px-3 py-2 pr-9 rounded-md border-b border-gray-200 focus:outline-none"
                               @keydown="handleKeydownEvents"/>

                        <!--Button to clear the filter text-->
                        <button @click.prevent="clearFilterText" v-if="filterText.length >= 1"
                                class="z-50 absolute right-5 top-5 text-gray-400 hover:text-brand cursor-pointer transition duration-300 pl-5">
                            <icon-x-circle class="w-4 h-4"></icon-x-circle>
                        </button>
                    </div>

                    <!--If there are options to display this is where we will render them.-->
                    <div v-if="sortedOptions.length"
                         :id="dropdownId"
                         class="max-h-60 overscroll-none overflow-y-auto scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-indigo-100">

                        <div v-for="(option, index) in sortedOptions"
                             :key="`${option.id}-${index}`"
                             class="px-2 py-1 my-1 mr-2 rounded-md cursor-pointer flex items-center font-normal"
                             :class="[activeIndex === index ? 'bg-indigo-100'
                         : (isSelected(option) ? 'bg-indigo-50' : 'bg-white'), isSelected(option) ? 'font-medium text-indigo-600' : '', 'hover:bg-gray-100']"
                             @click.prevent="toggleSelection(option)">
                            <!--If there is a search and it matches there will be a highlighted label property
                            On the object, and then we use v-html to make sure that gets rendered properly-->
                            <span v-if="option.highlightedLabel" v-html="option.highlightedLabel"></span>
                            <span v-else v-html="option.label"></span>
                        </div>
                    </div>

                    <div v-if="loading" class="flex justify-center items-center my-2">
                        <icon-refresh class="mr-1 h-5 w-5 text-indigo-600 animate-spin"/>
                        Loading...
                    </div>

                    <div v-else-if="!loading && !sortedOptions.length" class="flex justify-center text-gray-600 my-2">
                        No results found
                    </div>

                    <secondary-button v-if="selectedOptions.length && selectedOptions.length > 1"
                                      @click="clearSelection" class="w-full justify-center">
                        Unselect All
                    </secondary-button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { createPopper } from "@popperjs/core";

export default {
    name: "MultiSelectDropdown",

    props: {
        searchType: {
            required: false,
            type: String,
            default: "local",
            validator: (value) => {
                // When selecting none, no search/filter box will appear in the dropdown.
                // When selecting remote the filter is applied remotely and logic for that should
                // Be handled by the controller. When selecting local, the list is filtered
                // client side. Typically good idea if you are passing in a fixed array of options.
                const validTypes = ["local", "remote", "none"];
                const isValid = validTypes.includes(value);
                if (!isValid) console.error(`Invalid searchType: "${value}". Must be one of: ${validTypes.join(", ")}`);
                return isValid;
            }
        },
        options: {
            type: Array,
            description: "This prop can be used to pass in options if you are not doing a remote search. Either an array of strings or an array of objects.",
        },
        uri: {
            type: String,
            description: "The URI to fetch the options from when using remote search. Note that 'required' validation happens in the mounted hook.",
        },
        value: {
            required: true,
            type: Array,
            description: "The value to use for v-model bindings.",
        },
        visibleSelectionCount: {
            type: Number,
            default: 2,
            description: "The max number of selected items to display on the button when multiple selections are made.",
        },
        pinSelections: {
            type: Boolean,
            default: true,
            description: "When enabled selected options will be removed from main list and be pinned at the top.",
        },
        onlyFilterUnselected: {
            type: Boolean,
            default: false,
            description: "When enabled searching will always display the full selection list at the top and filters are only applied to the rest of the unselected options.",
        },
        objectValueKeyName: {
            type: String,
            default: "id",
            description: "The object key to use as value name to use when the list is a list of objects.",
        },
        objectLabelKeyName: {
            type: String,
            description: "The object key to use as label name to use when the list is a list of objects",
        },
        buttonText: {
            type: String,
            default: "Options",
            description: "The label that will be displayed on the button when no selection is made yet.",
        },
        buttonClass: {
            description: "Refers to the options button classes applied to the main button. Allows you to override the default styling.",
            default: () => "relative justify-center inline-flex text-left w-full outline-none transition duration-300 rounded-md justify-between items-center " +
                "border border-gray-300 shadow-sm py-2 bg-white font-normal text-gray-700 pl-3 pr-2 focus-within:ring-1 focus-within:ring-indigo-600 " +
                "focus-within:border-indigo-600 text-gray-600 focus-within:text-indigo-600 hover:border-indigo-300 transition duration-300",
        },
        minWidth: {
            type: Number,
            default: 140,
            description: "The minimum width in pixels of the button component",
        },
        hoverLabelClass: {
            description: "Refers to the classes applied to the small hover label when options are selected main button.",
            default: () => "whitespace-nowrap absolute -top-2 left-2 -mt-px inline-block px-1 bg-white hover:text-indigo-600 text-xs font-medium select-none"
        },
        hoverLabelValue: {
            type: String,
            description: "The text that is displayed in the hover label when options are selected."
        },
    },

    data () {
        return {
            // We also to generate a unique id to use for the dropdown scrollable container in order
            // to reset the scroll position when the dropdown closes. See function hidePopup.
            dropdownId: `dropdownScrollContainer-${Math.random().toString(36).substring(2, 11)}`,

            loading: false,
            show: false,
            mutableOptions: [],
            selectedOptions: [],
            filterText: "",
            activeIndex: -1,
            popperInstance: null,
        };
    },

    mounted () {
        if (this.searchType === "remote" && !this.uri) {
            throw new Error("When using remote search the URI must be provided in the props!");
        }

        if (this.options && this.searchType === "remote") {
            throw new Error("You cannot use remote search when using the options prop.");
        }

        if (this.options && this.options.length > 0) {
            this.mutableOptions = this.normaliseOptions(this.options);
        }

        if (!this.options && this.uri) {
            this.doSearch();
        }

        this.setupPopperInstance();
    },

    watch: {
        filterText (newVal) {
            if (newVal == null || newVal.length === 0) {
                // We have some reactivity problems when clearing the filter
                // so we have to manually remove all the previously set highlight labels.
                this.clearHighlights();
            }

            if (this.searchType === "remote") {
                this.doSearch();
            }
            this.activeIndex = -1;
        },
    },

    computed: {
        sortedOptions () {
            const search = this.filterText.trim().toLowerCase();
            const options = this.pinSelections
                ? this.sortedObjectOptions
                : this.filteredOptions;

            // If there's no search term, return the filtered options without any highlighting
            if (!search) {
                return options;
            }

            // Highlight the matching parts and return the sorted options
            // Apply the search and highlight matching parts
            return options.map(option => {
                // "option" might be a string, number, or object
                const rawLabel = option.label;

                // Convert anything (including numbers) to string
                const label = rawLabel != null ? String(rawLabel) : "";

                // If it's empty or null, skip highlight
                if (!label) return option;

                // Build up the highlight classes for selected VS unselected options
                const isSelected = this.isSelected(option);
                let highlightClasses = isSelected
                    ? "bg-indigo-300 rounded text-indigo-700 font-bold"
                    : "bg-red-100 rounded text-brand font-bold";

                // Example: If the user typed "Gra", the regex will be /(gra)/gi
                let regex = new RegExp(`(${this.filterText.trim()})`, "gi");
                let highlightedLabel = label.replace(regex, `<span class="${highlightClasses}">$1</span>`);

                // add the highlighted label to the object so we can access it in our v-html
                return {...option, highlightedLabel};
            });
        },

        // Handles sorting of objects when using pinning
        sortedObjectOptions () {
            const search = this.filterText.trim().toLowerCase();

            // Filter options based on the search text
            let allOptions = [...this.mutableOptions, ...this.selectedOptions];
            const options = allOptions.filter(option =>
                !search || option.label.toLowerCase().includes(search)
            );

            // Separate selected options from the filtered ones
            const selectedOptions = this.onlyFilterUnselected ? this.selectedOptions :
                this.selectedOptions.filter(option =>
                    options.some(opt => opt.id === option.id)
                );

            // Sort the selected options alphabetically by label
            const sortedSelectedOptions = selectedOptions.sort((a, b) =>
                a.label.localeCompare(b.label)
            );

            // Filter out selected options from the available options
            const unselectedOptions = options.filter(option =>
                !selectedOptions.some(selected => selected.id === option.id)
            );

            // Return sorted selected options followed by sorted unselected options
            return [...sortedSelectedOptions, ...unselectedOptions];
        },

        filteredOptions () {
            const search = this.filterText.trim().toLowerCase();
            // If no search is currently being done, return everything
            if (!search) return this.mutableOptions;

            // Filter out items that don't match the current search filter
            return this.mutableOptions.filter(option => {
                const label = option.label;
                // Ensure label is a string that includes the search text
                return label && typeof label === "string" && label.toLowerCase().includes(search);
            });
        },

        selectedOptionsText () {
            const limit = 2;
            const displayed = this.selectedOptions.slice(0, limit).map((option) => {
                return String(option.label);
            });
            const remaining = this.selectedOptions.length - limit;
            return remaining > 0
                ? displayed.join(", ") + ", +" + remaining + " more"
                : displayed.join(", ");
        }
    },

    methods: {
        doSearch: _.debounce(function () {
            this.loading = true;
            const searchParams = this.searchType === "remote" ? {filter: this.filterText} : null;
            axios.get(this.uri, {params: searchParams})
            .then((response) => {
                this.mutableOptions = this.normaliseOptions(response.data);
                this.loading = false;
            }).catch((error) => {
                this.loading = false;
                toastr.error("Failed to fetch options. Error: " + error.message);
            });
        }, 300),

        normaliseOptions (options) {
            // Here is where the magic happens and where we make sure that
            // the list of objects being used or the list of strings being used
            // are all converted to objects that have an id and a label so that we
            // can use those with confidence throughout the rest of the component
            return options.map((option, index) => {
                if (typeof option === "string") {
                    return {id: index, label: option};
                } else {
                    return {
                        ...option,
                        // assume each object has `id` or 'uuid' or fallback to index
                        id: option.id ?? option.uuid ?? index,
                        // If objectLabelKeyName has been set use that.
                        // Else we assume each object has name or label property we can use as the label
                        // Or fallback to "No Label" if all else fails :)
                        label: (this.objectLabelKeyName && option[this.objectLabelKeyName]) ?? option.label ?? option.name ?? "No Label"
                    };
                }
            });
        },

        openPopup () {
            this.show = !this.show;
            if (this.show) {
                this.$nextTick(() => this.popperInstance.update());
                this.activeIndex = -1;
            }
        },

        hidePopup () {
            this.show = false;

            this.$nextTick(() => {
                let scrollContainer = document.getElementById(this.dropdownId);
                if (scrollContainer) {
                    // Reset scroll position so it starts at the top next
                    // time we open the dropdown
                    scrollContainer.scrollTop = 0;
                }
            });
        },

        handleKeydownEvents (event) {
            // Unable to use event.preventDefault() before the switch as it will prevent the input from receiving the keydown event
            switch (event.key) {
                case "ArrowDown":
                    event.preventDefault();
                    this.navigateOptions(1);
                    break;
                case "ArrowUp":
                    event.preventDefault();
                    this.navigateOptions(-1);
                    break;
                case "Enter":
                    event.preventDefault();
                    this.selectOption();
                    break;
            }
        },

        navigateOptions (direction) {
            if (!this.sortedOptions.length) return;

            if (this.activeIndex === -1) {
                this.activeIndex = direction === 1 ? 0 : this.sortedOptions.length - 1;
            } else {
                this.activeIndex += direction;
                if (this.activeIndex >= this.sortedOptions.length) {
                    this.activeIndex = 0;
                } else if (this.activeIndex < 0) {
                    this.activeIndex = this.sortedOptions.length - 1;
                }
            }

            // Ensure selected option is visible
            this.$nextTick(() => {
                const activeElement = this.$el.querySelector(`.px-2:nth-child(${this.activeIndex + 1})`);
                if (activeElement) {
                    activeElement.scrollIntoView({behavior: "smooth", block: "nearest"});
                }
            });
        },

        selectOption () {
            if (this.activeIndex === -1) {
                return;
            }

            const option = this.sortedOptions[this.activeIndex];
            this.toggleSelection(option);
        },

        toggleSelection (option) {
            let optionKey = option[this.objectValueKeyName];
            let index = this.selectedOptions.findIndex((item) => item[this.objectValueKeyName] === optionKey);

            if (index === -1) {
                this.selectedOptions.push(option);
            } else {
                this.selectedOptions.splice(index, 1);
            }

            // Our value that we emit is an array of the keys of the items selected
            // based on the objectValueKeyName you chose. Typically, this would be id meaning
            // the value of the component would be an array of object id's of the selected objects
            this.$emit("input", this.selectedOptions.map((opt) => opt[this.objectValueKeyName]));
            // After clicking we don't want the search box to lose focus
            if (this.searchType !== "none") {
                this.$refs.search_input.focus();
            }
        },

        isSelected (option) {
            return this.selectedOptions.some((item) => {
                return item.id === option.id;
            });
        },

        clearSelection () {
            this.clearFilterText();
            this.selectedOptions = [];
            this.show = false;
            this.$emit("input", []);
        },

        clearFilterText () {
            this.filterText = "";

            if (this.options && this.mutableOptions.length === 0) {
                this.doSearch();
            }
        },

        clearHighlights () {
            this.selectedOptions.map((option) => {
                delete option.highlightedLabel;
                return {...option};
            });
        },

        setupPopperInstance () {
            const button = this.$refs.menuButtonContainer;
            const popup = this.$refs.menuPopup;
            if (!this.popperInstance) {
                this.popperInstance = createPopper(button, popup, {
                    placement: "bottom-start",
                    modifiers: [
                        {
                            name: "preventOverflow",
                            options: {resize: true, mainAxis: false},
                        },
                    ],
                });
            }
        },
    },
};
</script>
